import { connect } from 'react-redux';

import { Checkbox, Hint, Label, Select } from 'components';
import { useTranslations } from 'next-intl';
import { FC, useCallback, useMemo } from 'react';
import { Option } from 'types/Option';
import { updateInputField } from './_actions';
interface ISelectEditors {
  targetLanguageCodes: string[];
  selectedEditors: {
    generalEditor: string;
    languageEditors: Record<string, string>;
    perLanguage: boolean;
  };
  editors: {
    id: string;
    name: string;
  }[];
  isEditor: boolean;
  updateInputField: (payload: { field: string; value: any }) => void;
}

const SelectEditors: FC<ISelectEditors> = ({
  targetLanguageCodes,
  selectedEditors,
  editors,
  isEditor,
  updateInputField
}) => {
  const t = useTranslations();

  const changeGeneralEditor = useCallback(
    (editor: string) => {
      updateInputField({
        field: 'selectedEditors',
        value: {
          ...selectedEditors,
          generalEditor: editor
        }
      });
    },
    [selectedEditors, updateInputField]
  );

  const changeSingleEditor = useCallback(
    (editor: string, languageCode: string) => {
      updateInputField({
        field: 'selectedEditors',
        value: {
          ...selectedEditors,
          languageEditors: {
            ...selectedEditors.languageEditors,
            [languageCode]: editor
          }
        }
      });
    },
    [selectedEditors, updateInputField]
  );

  const togglePerLanguage = useCallback(() => {
    updateInputField({
      field: 'selectedEditors',
      value: {
        ...selectedEditors,
        perLanguage: !selectedEditors.perLanguage
      }
    });
  }, [selectedEditors, updateInputField]);

  const handlerSelectEditor =
    (language: string) =>
    ({ value }: Option) =>
      changeSingleEditor(value as string, language);

  const handlerSelectGeneralEditor = ({ value }: Option) => changeGeneralEditor(value as string);

  const hasNonDefaultlanguageEditors = useMemo(
    () =>
      Object.entries(selectedEditors.languageEditors).filter(
        ([, editor]) => editor && editor.length
      ).length > 0,
    [selectedEditors.languageEditors]
  );

  const shouldShowChangeEditorWarning = useMemo(
    () =>
      isEditor &&
      ((!selectedEditors.perLanguage && selectedEditors.generalEditor.length > 0) ||
        (selectedEditors.perLanguage && hasNonDefaultlanguageEditors)),
    [isEditor, selectedEditors, hasNonDefaultlanguageEditors]
  );

  if (!editors || !editors.length) return null;

  const changeEditorWarning = (
    <p className="texplanation texplanation--warning">
      <span className="ticon-warning mr-1"></span>
      {t('ordering_form.assigned_editor_warning')}
    </p>
  );

  const perLanguageCheckbox = (
    <div className="mt-2">
      <Checkbox
        checked={selectedEditors.perLanguage}
        onChange={togglePerLanguage}
        label={t('ordering_form.send_to_different_editors')}
      />
    </div>
  );

  if (selectedEditors.perLanguage)
    return (
      <div>
        <Label>{t('ordering_form.assigned_editor')}</Label>
        {targetLanguageCodes?.map((language) => (
          <div key={language} className="ordering_form__split_inputs mb-2">
            <div className="ordering_form__split_input">{t(`languages.${language}` as any)}</div>
            <div className="ordering_form__split_input">
              <Select
                selectedValue={selectedEditors.languageEditors[language] || ''}
                onChange={handlerSelectEditor(language)}
                options={[
                  { value: '', label: t('ordering_form.current_user_account') },
                  ...editors.map((editor) => ({ value: editor.id, label: editor.name }))
                ]}
              />
            </div>
          </div>
        ))}
        <Hint className="mb-2">{t('ordering_form.assigned_editor_explanation')}</Hint>
        {perLanguageCheckbox}
        {shouldShowChangeEditorWarning ? changeEditorWarning : null}
      </div>
    );

  return (
    <div>
      <Label>{t('ordering_form.assigned_editor')}</Label>
      <Select
        selectedValue={selectedEditors.generalEditor}
        onChange={handlerSelectGeneralEditor}
        options={[
          { value: '', label: t('ordering_form.current_user_account') },
          ...editors.map((editor) => ({ value: editor.id, label: editor.name }))
        ]}
      />
      <Hint className="mb-2">{t('ordering_form.assigned_editor_explanation')}</Hint>
      {targetLanguageCodes.length > 1 ? perLanguageCheckbox : null}
      {shouldShowChangeEditorWarning ? changeEditorWarning : null}
    </div>
  );
};

export default connect(
  ({ inputs: { selectedEditors, targetLanguageCodes }, staticData: { editors, isEditor } }) => ({
    selectedEditors,
    targetLanguageCodes,
    editors,
    isEditor
  }),
  { updateInputField }
)(SelectEditors);
